import React from "react";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";

import Link from "@mui/material/Link";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import GetAppIcon from "@mui/icons-material/GetApp";
import QrCode2Icon from "@mui/icons-material/QrCode2";

import { StyledSwitch } from "../../components/Base";
import Button from "../../components/Button";
import { getSSID, fetchAndDownloadFile } from "../../api";
import orderStatusService from "../../services/orderStatus";
import paymentTypeService, { hasQrBill } from "../../services/paymentType";

function formatDate(string) {
  return new Date(string).toLocaleString("de-CH");
}

async function downloadInvoiceFile(filename, onNotFound) {
  const ssid = await getSSID();
  const url = `${process.env.REACT_APP_API_URL}/files/download/invoice/${filename}?ssid=${ssid}`;
  await fetchAndDownloadFile(url, onNotFound);
}

const OrderItem = ({ onDelete, onRefund, onStatusChange, order }) => {
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();

  const handleNotFound = () => enqueueSnackbar(t("orders.document_not_found"), { variant: "error" });

  const downloadOrder = (filename) => downloadInvoiceFile(filename, handleNotFound);

  const downloadQRCode = (filename) => {
    const qrCodeFilename = filename.replace("Invoice_NGFT_", "Invoice_NGFT_QR_");
    return downloadInvoiceFile(qrCodeFilename, handleNotFound);
  };

  return (
    <TableRow key={order.order_id}>
      <TableCell>{order.order_id}</TableCell>
      <TableCell>{order.company_name}</TableCell>
      <TableCell>{order.buyer}</TableCell>
      <TableCell>
        {order.items.map((d, index) => (
          <li key={`${order.order_id}-${index}`}>
            {d.course_name} - {d.user_name}
          </li>
        ))}
      </TableCell>
      <TableCell>{paymentTypeService.getByCode(order.payment_type)?.getDisplayName(t) || order.payment_type}</TableCell>
      <TableCell>{order.total}</TableCell>

      <TableCell>{formatDate(order.created_at)}</TableCell>
      <TableCell>{orderStatusService.getByCode(order.status)?.getDisplayName(t) || order.status}</TableCell>
      <TableCell>
        <StyledSwitch
          title={t("orders.set_to_paid")}
          checked={order.status === "paid"}
          onChange={() => onStatusChange(order.order_id, order.status === "paid" ? "processing" : "paid")}
        />
      </TableCell>
      <TableCell>
        <Button
          title={t("orders.download_invoice")}
          icon={<GetAppIcon />}
          onClick={() => downloadOrder(order.invoice_name)}
        />
        {hasQrBill(order.payment_type) && (
          <Button
            title={t("orders.download_qr_code")}
            icon={<QrCode2Icon />}
            onClick={() => downloadQRCode(order.invoice_name)}
          />
        )}

        <Link onClick={() => onRefund(order.order_id, order.buyer_id)}>
          <Button title={t("orders.refund_order")} icon={<ArrowBackIcon />} />
        </Link>

        <Link onClick={() => onDelete(order.order_id, order.buyer_id)}>
          <Button title={t("orders.delete_order")} icon={<DeleteForeverIcon color="error" />} />
        </Link>
      </TableCell>
    </TableRow>
  );
};

export default OrderItem;
